const DEFAULT_MIN_DELAY_MS = 80;
const DEFAULT_MAX_DELAY_MS = 250;
const DEFAULT_FLUSH_INTERVAL_MS = 30000;
const DEFAULT_MAX_BUFFER = 20;

function nowMs() {
  if (typeof performance !== "undefined" && typeof performance.now === "function") {
    return performance.now();
  }
  return Date.now();
}

function scheduleOnFrame(callback) {
  if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
    window.requestAnimationFrame(callback);
    return;
  }
  setTimeout(callback, 0);
}

export function createCoalescedRenderScheduler(renderFn, options = {}) {
  const minDelayMs = Number.isFinite(options.minDelayMs) ? Number(options.minDelayMs) : DEFAULT_MIN_DELAY_MS;
  const maxDelayMs = Number.isFinite(options.maxDelayMs) ? Number(options.maxDelayMs) : DEFAULT_MAX_DELAY_MS;
  const label = String(options.label || "render");

  let lastRunAt = 0;
  let pendingTimer = null;
  let queuedMeta = null;
  let pendingSince = 0;

  function execute() {
    pendingTimer = null;
    const meta = queuedMeta || {};
    queuedMeta = null;
    lastRunAt = nowMs();
    try {
      renderFn(meta);
    } catch (error) {
      console.error(`[UI Perf] Scheduled render failed (${label}):`, error);
    }
  }

  return function schedule(meta = {}) {
    const immediate = meta.immediate === true;
    queuedMeta = meta;

    if (immediate) {
      if (pendingTimer) {
        clearTimeout(pendingTimer);
        pendingTimer = null;
      }
      scheduleOnFrame(execute);
      return;
    }

    const current = nowMs();
    if (!pendingSince) {
      pendingSince = current;
    }

    const sinceLastRun = current - lastRunAt;
    const nextDelay = Math.max(0, minDelayMs - sinceLastRun);
    const waited = current - pendingSince;
    const cappedDelay = waited >= maxDelayMs ? 0 : nextDelay;

    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    pendingTimer = setTimeout(() => {
      pendingSince = 0;
      scheduleOnFrame(execute);
    }, cappedDelay);
  };
}

function resolveMetricsEndpoint() {
  const projectId = window.firebaseConfig && window.firebaseConfig.projectId ?
    String(window.firebaseConfig.projectId) :
    "what-to-eat-e5ce9";
  return `https://us-central1-${projectId}.cloudfunctions.net/logError`;
}

function isLocalhostRuntime() {
  try {
    const hostname = String(window.location && window.location.hostname ? window.location.hostname : "").toLowerCase();
    return hostname === "localhost" || hostname === "127.0.0.1";
  } catch (error) {
    return false;
  }
}

function shouldPostMetrics(endpoint) {
  if (isLocalhostRuntime()) {
    const allowRemote = window.uiMetricsConfig && window.uiMetricsConfig.allowRemoteOnLocalhost === true;
    if (!allowRemote) return false;
  }
  return Boolean(endpoint);
}

export function createUiMetricTracker(options = {}) {
  const endpoint = String(options.endpoint || resolveMetricsEndpoint());
  const flushIntervalMs = Number.isFinite(options.flushIntervalMs) ?
    Number(options.flushIntervalMs) :
    DEFAULT_FLUSH_INTERVAL_MS;
  const maxBuffer = Number.isFinite(options.maxBuffer) ?
    Number(options.maxBuffer) :
    DEFAULT_MAX_BUFFER;
  const source = String(options.source || "ui-performance");
  const canPostRemotely = shouldPostMetrics(endpoint);

  let buffer = [];
  let flushTimer = null;

  async function flushInternal() {
    if (!buffer.length) return;
    if (!canPostRemotely) {
      buffer = [];
      return;
    }
    const payload = {
      type: "ui_metrics",
      source,
      recordedAt: new Date().toISOString(),
      metrics: buffer.slice(0, maxBuffer),
    };
    buffer = [];

    try {
      if (typeof window.getFirebaseAppCheckToken !== "function") {
        return;
      }
      const appCheckToken = await window.getFirebaseAppCheckToken();
      if (!appCheckToken || !appCheckToken.token) {
        return;
      }
      await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Firebase-AppCheck": appCheckToken.token,
        },
        keepalive: true,
        body: JSON.stringify(payload),
      });
    } catch (error) {
      console.warn("[UI Perf] Failed to send UI metrics:", error && error.message ? error.message : error);
    }
  }

  function scheduleFlush() {
    if (flushTimer) return;
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushInternal();
    }, flushIntervalMs);
  }

  return {
    record(eventName, payload = {}) {
      buffer.push({
        eventName: String(eventName || "event"),
        payload,
        timestamp: new Date().toISOString(),
      });
      if (buffer.length >= maxBuffer) {
        flushInternal();
        return;
      }
      scheduleFlush();
    },
    flush() {
      return flushInternal();
    },
  };
}

if (typeof window !== "undefined") {
  window.WTEUiPerformance = {
    createCoalescedRenderScheduler,
    createUiMetricTracker,
  };
}
